import { http } from "./client";
import type { PlatformAdminGrant, User } from "@/types/domain";

// "Super Admin Management" - create/list/suspend/reactivate/reset-password
// for permanent accounts, generate/list/revoke temporary access. Creating a
// Super Admin (permanent or temporary) is the one deliberate, narrow
// exception to the backend's blanket refusal to assign the super_admin
// role anywhere else - see PlatformAdminService's own doc comment on the
// backend for the full reasoning; SetStatus/ResetPassword/Delete below are
// unchanged, the same generic user-management handlers the tenant Users &
// Roles page uses.
export interface CreatePlatformAdminInput {
  full_name: string;
  email: string;
  username: string;
  mfa_required: boolean;
}

export interface CreateTemporaryPlatformAdminInput extends CreatePlatformAdminInput {
  valid_from: string;
  valid_until: string;
}

export interface PlatformAdminCreateResponse {
  user: User;
  grant: PlatformAdminGrant;
  activation_key: string;
  notice: string;
}

export const platformAdminsApi = {
  list: (filters: { search?: string; status?: string; per_page?: number } = {}) =>
    http.getPage<User & { grant?: PlatformAdminGrant }>("/platform/admins", filters),

  createPermanent: (input: CreatePlatformAdminInput) =>
    http.post<PlatformAdminCreateResponse>("/platform/admins", input),

  createTemporary: (input: CreateTemporaryPlatformAdminInput) =>
    http.post<PlatformAdminCreateResponse>("/platform/admins/temporary", input),

  listGrants: (filters: { access_type?: string; status?: string; per_page?: number } = {}) =>
    http.getPage<PlatformAdminGrant>("/platform/admins/grants", filters),

  revokeGrant: (id: string, reason: string) =>
    http.delete<void>(`/platform/admins/grants/${id}`, { reason }),

  setStatus: (id: string, status: "active" | "suspended") =>
    http.patch<void>(`/platform/admins/${id}/status`, { status }),

  resetPassword: (id: string) =>
    http.post<{ temporary_password: string; notice: string }>(`/platform/admins/${id}/reset-password`),

  remove: (id: string) => http.delete<void>(`/platform/admins/${id}`),

  // Unauthenticated - the activation key itself is the credential. See
  // PlatformAdminService.Redeem on the backend.
  redeemActivation: (key: string, password: string) =>
    http.post<{ user: User; notice: string }>("/platform/admin-activations/redeem", { key, password }),
};
